import { Box, FormField, Input, Text, Divider } from '@wix/design-system';
import React, { useContext, useState } from 'react';
import { RicosContext, ModalContext, EditorContext, ZIndexContext, usePluginExportsContext } from 'ricos-context';
import { PluginContainerData_Alignment } from 'ricos-schema';
import { SidePanelLayout, ImageNew, ToggleButton, FooterActions } from 'wix-rich-content-ui-components';
import { imageModals } from '../consts.js';
import { SETTINGS_IMAGE_PREVIEW, SETTINGS_CAPTION_INPUT, SETTINGS_ALT_INPUT_TEXT, SETTINGS_DISABLE_EXPAND_TOGGLE, SETTINGS_DISABLE_DOWNLOAD_TOGGLE, SETTINGS_SPOILER_TOGGLE } from '../dataHooks.js';
import { IMAGE_TYPE } from '../types.js';
import { useSpoilerPlugin } from './use-spoiler-plugin.js';
import 'ricos-content';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const Settings = ({ nodeId, config }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalContext) || {};
  const { getEditorCommands } = useContext(EditorContext);
  const zIndexService = useContext(ZIndexContext);
  const exports = usePluginExportsContext();
  const [initialData] = useState(
    () => getEditorCommands().getBlockComponentData(nodeId)
  );
  const [state, setState] = useState(
    () => fromComponentData({ componentData: initialData, config })
  );
  const isSpoilerEnabled = useSpoilerPlugin();
  const closeModal = () => {
    modalService.closeModal(imageModals.settings);
  };
  const handleOnChange = (value) => {
    getEditorCommands().setBlock(
      nodeId,
      IMAGE_TYPE,
      toComponentData({
        componentData: initialData,
        config,
        formValue: __spreadValues(__spreadValues({}, state), value)
      })
    );
    setState((prev) => __spreadValues(__spreadValues({}, prev), value));
  };
  const requiredHeight = isMobile ? 157 : 240;
  const requiredWidth = requiredHeight;
  const AltTextComponent = (_b = (_a = exports == null ? void 0 : exports.aiIntegrations) == null ? void 0 : _a.image) == null ? void 0 : _b.altTextInput;
  const hasCaptionTriggerExport = !!((_d = (_c = exports == null ? void 0 : exports.aiIntegrations) == null ? void 0 : _c.image) == null ? void 0 : _d.captionTrigger);
  return /* @__PURE__ */ React.createElement(
    SidePanelLayout,
    {
      onCloseButtonClick: closeModal,
      header: { title: t("ImageSettings_Header") },
      content: {
        children: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", height: "100%", gap: "SP4" }, /* @__PURE__ */ React.createElement(
          ImageNew,
          {
            dataHook: SETTINGS_IMAGE_PREVIEW,
            alt: state.altText || t("ImagePlugin_Image_Preview_Alt"),
            src: ((_f = (_e = state.image) == null ? void 0 : _e.src) == null ? void 0 : _f.id) || ((_h = (_g = state.image) == null ? void 0 : _g.src) == null ? void 0 : _h.url) || "",
            width: Number((_i = state.image) == null ? void 0 : _i.width),
            height: Number((_j = state.image) == null ? void 0 : _j.height),
            requiredHeight,
            requiredWidth
          }
        ), !hasCaptionTriggerExport && /* @__PURE__ */ React.createElement(FormField, { label: t("ImageSettings_Caption_Label") }, /* @__PURE__ */ React.createElement(
          Input,
          {
            dataHook: SETTINGS_CAPTION_INPUT,
            placeholder: t("ImageSettings_Caption_Input_Placeholder"),
            value: state.caption,
            onChange: (e) => handleOnChange({ caption: e.target.value })
          }
        )), AltTextComponent ? /* @__PURE__ */ React.createElement(
          AltTextComponent,
          {
            image: state.image,
            altText: state.altText,
            onChange: (value) => handleOnChange({ altText: value })
          }
        ) : /* @__PURE__ */ React.createElement(
          FormField,
          {
            label: t("ImageSettings_Alt_Label"),
            infoContent: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP1" }, /* @__PURE__ */ React.createElement(Text, { light: true, size: "small" }, t("ImageSettings_Alt_Label_Tooltip"))),
            infoTooltipProps: {
              appendTo: (el) => {
                var _a2;
                return Boolean((_a2 = el.getAttribute("data-hook")) == null ? void 0 : _a2.includes("ricos-portal"));
              },
              zIndex: zIndexService.getZIndex("TOOLTIP")
            }
          },
          /* @__PURE__ */ React.createElement(
            Input,
            {
              dataHook: SETTINGS_ALT_INPUT_TEXT,
              type: "text",
              placeholder: t("ImageSettings_Alt_Input_Placeholder"),
              value: state.altText,
              onChange: (e) => handleOnChange({ altText: e.target.value })
            }
          )
        ), /* @__PURE__ */ React.createElement(
          ToggleButton,
          {
            title: t("ImagePlugin_Settings_ImageOpensInExpandMode_Label"),
            checked: !state.disableExpand,
            onChange: () => handleOnChange({ disableExpand: !state.disableExpand }),
            infoContent: t("ImageSettings_Expand_Mode_Toggle"),
            dataHook: SETTINGS_DISABLE_EXPAND_TOGGLE
          }
        ), /* @__PURE__ */ React.createElement(
          ToggleButton,
          {
            title: t("ImagePlugin_Settings_ImageCanBeDownloaded_Label"),
            checked: !state.disableDownload,
            onChange: () => handleOnChange({ disableDownload: !state.disableDownload }),
            infoContent: t("ImagePlugin_Settings_ImageCanBeDownloaded_Tooltip"),
            dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE
          }
        ), isSpoilerEnabled ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(
          ToggleButton,
          {
            title: t("ImageSettings_Spoiler_Toggle"),
            checked: Boolean(state.isMarkedAsSpoiler),
            onChange: () => handleOnChange({ isMarkedAsSpoiler: !state.isMarkedAsSpoiler }),
            infoContent: t("Spoiler_Toggle_Tooltip"),
            dataHook: SETTINGS_SPOILER_TOGGLE
          }
        )) : null)
      },
      footer: {
        children: /* @__PURE__ */ React.createElement(
          FooterActions,
          {
            onSave: closeModal,
            onCancel: () => {
              getEditorCommands().setBlock(nodeId, IMAGE_TYPE, initialData);
              closeModal();
            }
          }
        )
      }
    }
  );
};
const fromComponentData = ({
  componentData,
  config
}) => {
  var _a, _b, _c, _d, _e, _f, _g;
  return {
    image: componentData.image,
    caption: (_a = componentData.caption) != null ? _a : "",
    altText: (_b = componentData.altText) != null ? _b : "",
    disableExpand: (_c = componentData.disableExpand) != null ? _c : Boolean(config.disableExpand),
    disableDownload: (_d = componentData.disableDownload) != null ? _d : Boolean(config.disableDownload),
    isMarkedAsSpoiler: (_g = (_f = (_e = componentData.containerData) == null ? void 0 : _e.spoiler) == null ? void 0 : _f.enabled) != null ? _g : void 0
  };
};
const toComponentData = ({
  componentData,
  config,
  formValue
}) => {
  var _a, _b;
  const { disableExpand, disableDownload, caption, altText, image, isMarkedAsSpoiler } = formValue;
  const getValueOrDefault = (setting, value) => {
    if (setting !== void 0) {
      return setting === value ? void 0 : value;
    }
    return value === false ? void 0 : value;
  };
  return __spreadProps(__spreadValues({}, componentData), {
    image,
    caption: caption || void 0,
    altText: altText || void 0,
    disableExpand: getValueOrDefault(config.disableExpand, disableExpand),
    disableDownload: getValueOrDefault(config.disableDownload, disableDownload),
    containerData: __spreadProps(__spreadValues({
      alignment: (_b = (_a = componentData == null ? void 0 : componentData.containerData) == null ? void 0 : _a.alignment) != null ? _b : PluginContainerData_Alignment.LEFT
    }, componentData == null ? void 0 : componentData.containerData), {
      spoiler: (() => {
        var _a2;
        if (!isMarkedAsSpoiler) {
          return void 0;
        }
        return __spreadProps(__spreadValues({}, (_a2 = componentData == null ? void 0 : componentData.containerData) == null ? void 0 : _a2.spoiler), {
          enabled: isMarkedAsSpoiler
        });
      })()
    })
  });
};

export { Settings };
//# sourceMappingURL=settings.js.map
